<template>
  <div class="product-list-banners relative bg-white z-navbar md:z-2">
    <div v-if="appStore.isDesktopScreen" class="title container mx-auto" :class="!category.intro && hasBanners ? 'py-3' : 'pt-5'">
      <h1 class="text-xl font-500 flex items-center gap-4 md:text-2xl">
        {{ selectedRegionOption?.name }}
        <span v-if="categoryName" class="inline-block w-1px h-4 bg-gray-50" />
        {{ categoryName }}
      </h1>
    </div>
    <div v-if="category.intro" class="container mx-auto px-5 pt-1 pb-2 md:px-0" :class="hasBanners ? 'md:py-4' : 'md:py-3'">
      <h2 class="text-base md:text-lg font-400">{{ category.intro }}</h2>
    </div>
    <div class="relative">
      <product-list-banners-actions
        v-if="appStore.isDesktopScreen && hasBanners"
        v-show="!swiper?.isLocked"
        class="absolute inset-x-0 -top-11 container mx-auto flex justify-end"
        @prev="slidePrev"
        @next="slideNext"
      />
      <etg-swiper
        v-if="hasBanners"
        ref="etgSwiperRef"
        :data="banners"
        :autoplay="true"
        :swiper-options="swiperOptions"
        swiper-container-class="container"
        swiper-slide-class="w-[calc(100%-40px)] sm:w-134"
      >
        <template #default="{ item }">
          <banner-container :item="item" class="slider-wrapper flex-col justify-end" @click="handleBannerClick(item)">
            <etg-img
              v-if="item.cover_image || isShowBannerImage"
              class="object-cover rounded w-full aspect-2.67 mb-4"
              :class="[!item.cover_image && 'bg-transparent']"
              :src="`fn:${appStore.isMobile ? item.cover_image : item.large_cover_image || item.cover_image}`"
              :alt="item.title"
            />
            <h2 class="text-lg h-12 line-clamp-2">{{ item.description }}</h2>
          </banner-container>
        </template>
        <template #skeleton>
          <product-list-banners-skeleton />
        </template>
      </etg-swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProductListBannersSkeleton from './product-list-banners-skeleton.vue'
import ProductListBannersActions from './product-list-banners-actions.vue'
import EtgSwiper from '@/components/common/etg-swiper.vue'
import BannerContainer from '@/components/common/banner-container.vue'

import { useAppStore } from '@/store/app'
import { useRegionStore } from '@/store/region'
import type { EtgSwiperExposed } from '@/types/component'
import { GtmEvent } from '@/types/gtm'
import type { RegionBanner } from '@/types/api'
import { screens } from '@/theme.config'

const nuxtApp = useNuxtApp()

const appStore = useAppStore()
const regionStore = useRegionStore()

const { selectedRegionOption } = storeToRefs(regionStore)

const { category, categoryName } = useCategory()

const etgSwiperRef = ref<Nullable<EtgSwiperExposed>>(null)

const banners = computed(() => category.value.banners || [])
const hasBanners = computed(() => banners.value.length > 0)
const isShowBannerImage = computed(() => banners.value.length > 1 && banners.value.some(item => item.cover_image))

const gap = computed(() => (appStore.isLargeDesktopScreen ? 20 : 8))

const swiperOptions = computed(() => ({
  slidesPerView: 'auto' as const,
  slidesPerGroup: 1,
  spaceBetween: gap.value,
  centeredSlides: true,
  pagination: false,
  speed: 300,
  observer: true,
  loop: banners.value.length > 2,
  autoplay: banners.value.length > 2,
  breakpoints: {
    [screens.md]: {
      centeredSlides: false,
    },
  },
}))

const swiper = computed(() => etgSwiperRef.value?.swiper)

function slidePrev() {
  swiper.value?.slidePrev()
}

function slideNext() {
  swiper.value?.slideNext()
}

function handleBannerClick(item: RegionBanner) {
  const payload = {
    'Banner ID': item.id,
    'Banner Title': item.title,
    Path: item.link_target,
    Sequence: swiper.value?.clickedIndex,
  }
  nuxtApp.$trackEvent({
    event: GtmEvent.BANNER_CLICKED,
    payload,
  })
}
</script>
